import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { AlertTriangle, Calendar } from 'tabler-icons-react';

import { useCostTypeLabels } from 'constants/costTypeLabels';
import { usePalletsTypes } from 'constants/palletTypes';
import { Charge, SendPalletDTO } from 'interfaces';
import { getDayLabel, getValueLabel } from 'utils/functions';

import styles from './MobileCard.module.css';

type MobileCardProps = {
   data: SendPalletDTO | Charge;
   includesDamagedPallets?: boolean;
};

const MobileCard = ({ data, includesDamagedPallets }: MobileCardProps) => {
   const { t } = useTranslation();
   const chargeMode = 'chargeType' in data;

   const { PALLET_TYPE_LABELS } = usePalletsTypes();
   const { costTypeLabels } = useCostTypeLabels();

   return (
      <div className={styles.container}>
         {chargeMode ? (
            <>
               <div className={styles.cardRow}>
                  <span>{t('damaged_by_client.table.pallet_type')}</span>
                  <p>{data.palletType ? PALLET_TYPE_LABELS[data.palletType] : '-'}</p>
               </div>
               <div className={styles.cardRow}>
                  <span>{t('additional_costs_client.table.charge_type')}</span>
                  <p>{data.otherChargeName || costTypeLabels[data.chargeType]}</p>
               </div>
               <div className={styles.cardRow}>
                  <span>{t('additional_costs_client.table.period')}</span>
                  <p>
                     {data.retentionPeriodDays
                        ? `${data.retentionPeriodDays} ${getDayLabel(data.retentionPeriodDays)}`
                        : '-'}
                  </p>
               </div>
               <div className={styles.cardRow}>
                  <span>{t('additional_costs_client.table.pallets_amount')}</span>
                  <p>{data.palletsAmount || '-'}</p>
               </div>
            </>
         ) : (
            <>
               <div className={classNames(styles.cardRow, styles.topRow)}>
                  <span>
                     <span>{t('send_damaged_pallets.table.send_date')}</span>
                     <p className={styles.calendarRow}>
                        <Calendar />
                        {data.dateOfShipment}
                     </p>
                  </span>
                  {includesDamagedPallets && <AlertTriangle color={'var(--primary-red)'} />}
               </div>
               <div className={styles.cardRow}>
                  <span>{t('mobileCard.networkOrderNumber')}</span>
                  <p>{getValueLabel(data.externalCommercialNetworkNumber)}</p>
               </div>
               <div className={styles.cardRow}>
                  <span>{t('send_damaged_pallets.table.network_name')}</span>
                  <p>{data.displayName}</p>
               </div>
               <div className={styles.cardRow}>
                  <span>{t('send_damaged_pallets.table.network_address')}</span>
                  <p>{data.displayAddress}</p>
               </div>
               {data.pallets.map((pallet, index) => (
                  <div key={index} className={styles.palletRow}>
                     <div>
                        <span>{t('damaged_by_client.table.pallet_type')}</span>
                        <p>{PALLET_TYPE_LABELS[pallet.palletType]}</p>
                     </div>
                     <div>
                        <span>{t('additional_costs_client.table.pallets_amount')}</span>
                        <p>{pallet.amount}</p>
                     </div>
                  </div>
               ))}
            </>
         )}
      </div>
   );
};

export default MobileCard;
